<script lang="ts">
  import { usePage } from '@inertiajs/svelte';
  import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
  } from '$lib/components/ui/dialog';
  import { Button } from '$lib/components/ui/button';
  import { Input } from '$lib/components/ui/input';
  import { Alert, AlertDescription } from '$lib/components/ui/alert';
  import { Loader2, AlertCircle, Phone, KeyRound, ShieldAlert } from 'lucide-svelte';

  // 1. 使用 Svelte 5 的 $props() 接收回调
  let { onSuccess = () => {} } = $props();

  // 2. 使用 Svelte 5 的 usePage() 取代旧版的 $page
  const page = usePage();
  const translations = $derived((page.props as any).translations || {});
  const t = $derived(translations.phone_binding || {});

  // 3. 模态框及表单 Runes 状态
  let open = $state(false);
  let isForced = $state(false); // 是否为强制模式

  let phone = $state('');
  let code = $state('');
  let countdown = $state(0);
  let errorMessage = $state('');
  let loading = $state(false);
  let timer: any = null;

  // 插值替换
  function interpolate(template = '', params: Record<string, any> = {}) {
    return template.replace(/%\{(\w+)\}/g, (_, key) => params[key] ?? '');
  }

  // 暴露给外部调用
  export function openModal(forced = false) {
    isForced = forced;
    open = true;
  }

  export function closeModal() {
    isForced = false;
    open = false;
  }

  // 核心阻断逻辑：防止通过点击遮罩层、按 ESC 或外部事件关闭
  function handleOpenChange(newOpenState: boolean) {
    if (!newOpenState && isForced) {
      // 强制模式下，阻止关闭
      open = true;
      return;
    }
    open = newOpenState;
  }

  // 发送验证码
  async function handleSendCode() {
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      errorMessage = t.errors?.invalid_phone || '请输入有效的 11 位手机号码';
      return;
    }

    errorMessage = '';
    loading = true;

    try {
      const response = await fetch('/phone_verification/send_code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({ phone })
      });

      const data = await response.json();

      if (response.ok) {
        startCountdown();
      } else {
        errorMessage = data.error || t.errors?.send_failed || '发送失败，请稍后重试';
      }
    } catch (err) {
      errorMessage = t.errors?.network_error || '网络繁忙，请稍后重试';
    } finally {
      loading = false;
    }
  }

  // 倒计时
  function startCountdown() {
    countdown = 60;
    if (timer) clearInterval(timer);
    timer = setInterval(() => {
      countdown -= 1;
      if (countdown <= 0) {
        clearInterval(timer);
      }
    }, 1000);
  }

  // 校验绑定
  async function handleVerify() {
    if (!code || code.length !== 6) {
      errorMessage = t.errors?.invalid_code || '请输入 6 位短信验证码';
      return;
    }

    errorMessage = '';
    loading = true;

    try {
      const response = await fetch('/phone_verification/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({ phone, code })
      });

      const data = await response.json();

      if (response.ok) {
        isForced = false;
        open = false; // 绑定成功关闭
        onSuccess?.();
      } else {
        errorMessage = data.error || t.errors?.verify_failed || '绑定失败';
      }
    } catch (err) {
      errorMessage = t.errors?.network_error || '网络错误';
    } finally {
      loading = false;
    }
  }
</script>

<Dialog open={open} onOpenChange={handleOpenChange}>
  <!--
    1. [&>button]:hidden 隐藏右上角的默认关闭 X 按钮
    2. onInteractOutside & onEscapeKeyDown：彻底阻止点击外部遮罩或按 ESC 关闭
  -->
  <DialogContent 
    class="sm:max-w-md [&>button]:hidden"
    onInteractOutside={(e) => { if (isForced) e.preventDefault(); }}
    onEscapeKeyDown={(e) => { if (isForced) e.preventDefault(); }}
  >
    <DialogHeader class="space-y-1">
      <div class="flex items-center gap-2 text-primary">
        <ShieldAlert class="h-5 w-5 text-amber-500" />
        <DialogTitle class="text-xl font-bold">{t.title}</DialogTitle>
      </div>
      <DialogDescription class="text-sm text-muted-foreground pt-1">
        {t.subtitle}
      </DialogDescription>
    </DialogHeader>

    {#if errorMessage}
      <Alert variant="destructive" class="py-2 px-3 text-sm flex items-center gap-2 my-2">
        <AlertCircle class="h-4 w-4 shrink-0" />
        <AlertDescription>{errorMessage}</AlertDescription>
      </Alert>
    {/if}

    <div class="space-y-4 pt-2">
      <!-- 手机号输入 -->
      <div class="relative">
        <Phone class="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="tel"
          bind:value={phone}
          placeholder={t.phone_placeholder}
          class="pl-9"
        />
      </div>

      <!-- 验证码输入 -->
      <div class="flex gap-2">
        <div class="relative flex-1">
          <KeyRound class="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            bind:value={code}
            placeholder={t.code_placeholder}
            maxlength={6}
            class="pl-9"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          onclick={handleSendCode}
          disabled={countdown > 0 || loading}
          class="shrink-0 min-w-[120px]"
        >
          {#if loading && countdown === 0}
            <Loader2 class="h-4 w-4 animate-spin mr-1" />
          {/if}
          {countdown > 0
            ? interpolate(t.resend_countdown, { seconds: countdown })
            : t.get_code}
        </Button>
      </div>

      <!-- 提交按钮 -->
      <Button
        type="button"
        onclick={handleVerify}
        disabled={loading}
        class="w-full mt-2"
      >
        {#if loading}
          <Loader2 class="h-4 w-4 animate-spin mr-2" />
        {/if}
        {t.confirm_and_publish}
      </Button>
    </div>
  </DialogContent>
</Dialog>